import { imageUrl, type MediaContext } from "@/features/media/mediaUrl";
import type { IconMapping, MapIcon } from "@/types";

/**
 * Registering the tenant's marker icons as Mapbox sprite images.
 *
 * Icon names are the file name without its extension, because that's the key the
 * `iconMapping` config joins on — `iconName: "museum"` refers to `museum.svg`.
 */

/** What Mapbox accepts as a sprite image, and what `loadImage` can yield. */
export type SpriteImage = ImageBitmap | HTMLImageElement | ImageData;

/** The slice of the Mapbox map API used here. */
export interface ImageHostMap {
  hasImage(id: string): boolean;
  addImage(id: string, image: SpriteImage): void;
  loadImage(url: string): Promise<{ data: SpriteImage }>;
}

/** `museum.svg` -> `museum`; a name with no extension is returned unchanged. */
export function iconNameFromFile(fileName: string): string {
  const dot = fileName.lastIndexOf(".");
  return dot > 0 ? fileName.slice(0, dot) : fileName;
}

/**
 * Every icon the project has, wherever it keeps them.
 *
 * Two shapes are in the wild. Older projects list them once at the top of the
 * config as `mapIcons`, which is what the legacy API served. Current ones attach
 * the artwork to each `iconMapping` entry instead — as a *list*, because an entry
 * carries its normal icon and its highlighted variant together, which is what
 * `iconNameHover`/`iconNameActive` then select between.
 *
 * Reading only the first meant a project on the current shape registered no
 * sprite images at all, so every `icon-image` reference resolved to nothing and
 * the markers came up blank.
 */
export function collectMapIcons({
  mapIcons,
  iconMapping,
}: {
  mapIcons: MapIcon[] | undefined;
  iconMapping: IconMapping[] | undefined;
}): MapIcon[] {
  const fromMapping = (iconMapping ?? []).flatMap((entry) => {
    const icon = entry.mapIcon;
    return Array.isArray(icon) ? icon : icon ? [icon] : [];
  });

  const byId = new Map<string, MapIcon>();
  for (const icon of [...(mapIcons ?? []), ...fromMapping]) {
    if (icon?.name && !byId.has(icon.name)) byId.set(icon.name, icon);
  }

  return [...byId.values()];
}

/**
 * The icon URL for each value of the project's icon field.
 *
 * What a 3D markers layer styles itself from. It draws into a WebGL scene rather
 * than through the style spec, so it can't reach the map's sprite and takes URLs
 * instead — and it keys them by the *value* it reads off a feature, not by icon
 * name, because the value is what it has to hand when drawing one.
 *
 * `iconMapping` is the join: `{ value: "Museum", iconName: "museum" }` against an
 * icon file called `museum.svg`.
 */
export function markerIconUrls({
  mapIcons,
  iconMapping,
  media,
}: {
  mapIcons: MapIcon[] | undefined;
  iconMapping: IconMapping[] | undefined;
  media: MediaContext;
}): Record<string, string> {
  const byIconName = new Map<string, string>();

  for (const icon of collectMapIcons({ mapIcons, iconMapping })) {
    const filePath = icon.image?.filePath;
    if (!icon.name || !filePath) continue;

    const url = imageUrl(filePath, "32x32", media);
    if (url) byIconName.set(iconNameFromFile(icon.name), url);
  }

  const urls: Record<string, string> = {};
  for (const { value, iconName } of iconMapping ?? []) {
    const url = iconName ? byIconName.get(iconName) : undefined;
    if (value && url) urls[value] = url;
  }

  return urls;
}

/**
 * Load every configured marker icon into the map's sprite.
 *
 * Icons are requested at 32×32 unless `iconMapping` marks one `useOriginal`, in
 * which case 100×100 preserves enough detail for an icon that is meant to be seen
 * at its authored proportions.
 *
 * Failures are logged and skipped: a missing icon should leave one marker
 * unstyled, not abort the whole sprite load and leave the map without any.
 */
export async function addImagesToMap(
  map: ImageHostMap,
  {
    mapIcons,
    iconMapping,
    media,
  }: {
    mapIcons: MapIcon[] | undefined;
    iconMapping: IconMapping[] | undefined;
    media: MediaContext;
  }
): Promise<void> {
  const icons = collectMapIcons({ mapIcons, iconMapping });
  if (icons.length === 0) return;

  const useOriginal = new Set(
    (iconMapping ?? [])
      .filter((entry) => entry.useOriginal && entry.iconName)
      .map((entry) => entry.iconName as string)
  );

  await Promise.all(
    icons.map(async (icon) => {
      const fileName = icon.name;
      const filePath = icon.image?.filePath;
      if (!fileName || !filePath) return;

      const id = iconNameFromFile(fileName);
      if (map.hasImage(id)) return;

      const size = useOriginal.has(id) ? "100x100" : "32x32";
      const url = imageUrl(filePath, size, media);
      if (!url) return;

      try {
        const { data } = await map.loadImage(url);
        // Another icon with the same name may have landed while this was in
        // flight; addImage would throw on the duplicate.
        if (!map.hasImage(id)) map.addImage(id, data);
      } catch (error) {
        console.error(`[addImagesToMap] failed to load icon "${id}"`, error);
      }
    })
  );
}
